import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, 
  Phone,
  Package,
  CheckCircle2,
  Loader2,
  Clock,
  MessageCircle,
  CreditCard,
  MapPin,
  Truck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('reservation');
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [dorms, setDorms] = useState([]);
  const [marketingSources, setMarketingSources] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reservationSubmitted, setReservationSubmitted] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    school_id: '',
    pickup_location: '',
    pickup_date: '',
    pickup_date_surcharge: 0,
    pickup_address: '',
    pickup_apartment: '',
    pickup_city: '',
    pickup_zip: '',
    delivery_location: '',
    delivery_date: '',
    delivery_date_surcharge: 0,
    delivery_address: '',
    delivery_apartment: '',
    delivery_city: '',
    delivery_zip: '',
    how_heard_about_us: '',
    how_heard_other: ''
  });

  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setIsProcessingPayment(true);
      pollPaymentStatus(sessionId);
    }
    fetchSchools();
    fetchMarketingSources();
  }, [searchParams]);

  const fetchSchools = async () => {
    try {
      const response = await axios.get(`${API}/schools`);
      setSchools(response.data);
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const fetchMarketingSources = async () => {
    try {
      const response = await axios.get(`${API}/marketing-sources`);
      setMarketingSources(response.data);
    } catch (error) {
      console.error('Error fetching marketing sources:', error);
    }
  };

  const fetchDorms = async (schoolId) => {
    try {
      const response = await axios.get(`${API}/schools/${schoolId}`);
      setDorms(response.data.dorms);
      setSelectedSchool(response.data);
    } catch (error) {
      console.error('Error fetching dorms:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      setIsProcessingPayment(false);
      toast.error('Payment verification timed out. Please contact us for help.');
      return;
    }
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setIsProcessingPayment(false);
        setReservationSubmitted(true);
        toast.success('Payment successful! Your reservation is confirmed.');
      } else if (response.data.status === 'expired') {
        setIsProcessingPayment(false);
        toast.error('Payment session expired. Please try again.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSchoolSelect = (schoolId) => {
    setFormData(prev => ({
      ...prev,
      school_id: schoolId,
      pickup_location: '',
      pickup_date: '',
      pickup_date_surcharge: 0,
      delivery_location: '',
      delivery_date: '',
      delivery_date_surcharge: 0,
      pickup_address: '', pickup_apartment: '', pickup_city: '', pickup_zip: '',
      delivery_address: '', delivery_apartment: '', delivery_city: '', delivery_zip: ''
    }));
    fetchDorms(schoolId);
  };

  const handlePickupLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      pickup_location: location,
      pickup_address: '', pickup_apartment: '', pickup_city: '', pickup_zip: ''
    }));
  };

  const handleDeliveryLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      delivery_location: location,
      delivery_address: '', delivery_apartment: '', delivery_city: '', delivery_zip: ''
    }));
  };

  const handleReservationSubmit = async (e) => {
    e.preventDefault();

    if (!formData.how_heard_about_us) {
      toast.error('Please tell us how you heard about us');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/reservations`, formData);

      const checkoutResponse = await axios.post(`${API}/checkout/create-session`, {
        reservation_id: response.data.id,
        origin_url: window.location.origin
      });

      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create reservation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactData(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactData);
      setContactSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Payment processing state
  if (isProcessingPayment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="payment-processing">
        <motion.div
          className="bg-white rounded-2xl p-8 md:p-12 text-center max-w-lg shadow-xl border border-gray-100"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-navy animate-spin" />
          </div>
          <h2 className="text-3xl font-bold text-navy mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Verifying Payment...
          </h2>
          <p className="text-gray-600">Please wait while we confirm your payment.</p>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (reservationSubmitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="reservation-success">
        <motion.div
          className="bg-white rounded-2xl p-8 md:p-12 text-center max-w-lg shadow-xl border border-gray-100"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-navy mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Reservation Confirmed!
          </h2>
          <p className="text-gray-600 mb-6">
            Your $30 deposit has been received. We'll send you a confirmation email with pickup details.
          </p>
          <Button
            onClick={() => {
              setReservationSubmitted(false);
              setFormData({
                first_name: '', last_name: '', email: '', phone: '',
                school_id: '', pickup_location: '', pickup_date: '',
                pickup_date_surcharge: 0,
                pickup_address: '', pickup_apartment: '', pickup_city: '', pickup_zip: '',
                delivery_location: '', delivery_date: '',
                delivery_date_surcharge: 0,
                delivery_address: '', delivery_apartment: '', delivery_city: '', delivery_zip: '',
                how_heard_about_us: '', how_heard_other: ''
              });
              setSelectedSchool(null);
              setDorms([]);
            }}
            className="btn-primary"
            data-testid="make-another-reservation-btn"
          >
            Make Another Reservation
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-white section-padding border-b border-gray-100" data-testid="contact-hero">
        <div className="section-container">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              Get Started
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-navy mb-6 leading-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Reserve Your Storage Today
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              $30 deposit secures your spot. Complete the form below to reserve your storage.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="bg-gray-50 py-6 border-b border-gray-100" data-testid="contact-info-bar">
        <div className="section-container">
          <div className="flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-red-600" />
              <a href="tel:+15407654298" className="font-medium text-navy hover:text-red-brand">
                +1 (540) 765-4298
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-red-600" />
              <a href="mailto:mandy@studentstoreit.com" className="font-medium text-navy hover:text-red-brand">
                mandy@studentstoreit.com
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-red-600" />
              <span className="text-navy">Mon - Sat: 9AM - 5PM</span>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="section-padding bg-white" data-testid="forms-section">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            {/* Tabs */}
            <div className="flex gap-4 mb-8 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('reservation')}
                className={`pb-4 px-4 font-medium transition-colors relative ${
                  activeTab === 'reservation' ? 'text-navy' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-reservation"
              >
                <Package className="w-4 h-4 inline-block mr-2" />
                Reserve Storage
                {activeTab === 'reservation' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-brand"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('contact')}
                className={`pb-4 px-4 font-medium transition-colors relative ${
                  activeTab === 'contact' ? 'text-navy' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-contact"
              >
                <MessageCircle className="w-4 h-4 inline-block mr-2" />
                Contact Us
                {activeTab === 'contact' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-brand"></div>
                )}
              </button>
            </div>

            {/* Reservation Form */}
            {activeTab === 'reservation' && (
              <motion.form
                onSubmit={handleReservationSubmit}
                className="space-y-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid="reservation-form"
              >
                {/* Personal Info */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-navy mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Personal Information
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        placeholder="John"
                        data-testid="input-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        placeholder="Doe"
                        data-testid="input-last-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@university.edu"
                        data-testid="input-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="(555) 123-4567"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* School Selection */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-navy mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    University
                  </h3>
                  <div className="space-y-2">
                    <Label>Select Your University *</Label>
                    <Select
                      value={formData.school_id}
                      onValueChange={handleSchoolSelect}
                    >
                      <SelectTrigger data-testid="select-school">
                        <SelectValue placeholder="Select your university" />
                      </SelectTrigger>
                      <SelectContent>
                        {schools.map((school) => (
                          <SelectItem key={school.id} value={school.id}>
                            {school.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Pickup Info */}
                {selectedSchool && (
                  <motion.div
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h3 className="text-lg font-bold text-navy mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      <Truck className="w-5 h-5 inline-block mr-2 text-red-600" />
                      Spring Pickup Details
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Pickup Location *</Label>
                        <Select
                          value={formData.pickup_location}
                          onValueChange={handlePickupLocationSelect}
                        >
                          <SelectTrigger data-testid="select-pickup-location">
                            <SelectValue placeholder="Select dorm / residence" />
                          </SelectTrigger>
                          <SelectContent>
                            {dorms.map((dorm) => (
                              <SelectItem key={dorm} value={dorm}>
                                {dorm}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Pickup Date *</Label>
                        <Select
                          value={formData.pickup_date}
                          onValueChange={(val) => {
                            const dateObj = selectedSchool.pickup_dates.find(d => d.date === val);
                            setFormData(prev => ({ ...prev, pickup_date: val, pickup_date_surcharge: dateObj?.surcharge || 0 }));
                          }}
                        >
                          <SelectTrigger data-testid="select-pickup-date">
                            <SelectValue placeholder="Select pickup date" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedSchool.pickup_dates.map((dateObj) => (
                              <SelectItem key={dateObj.date} value={dateObj.date}>
                                {new Date(dateObj.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                {dateObj.type === 'custom' ? ` (+$${dateObj.surcharge} custom)` : ' (Free)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {formData.pickup_location === 'Off-Campus' && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                        <p className="text-sm font-medium text-navy flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-red-600" />
                          Off-Campus Pickup Address
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2 sm:col-span-2">
                            <Label>Street Address *</Label>
                            <Input
                              name="pickup_address"
                              value={formData.pickup_address}
                              onChange={handleChange}
                              required
                              placeholder="123 Main Street"
                              data-testid="input-pickup-address"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Apt / Suite</Label>
                            <Input
                              name="pickup_apartment"
                              value={formData.pickup_apartment}
                              onChange={handleChange}
                              placeholder="Apt 4B"
                              data-testid="input-pickup-apartment"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>City *</Label>
                            <Input
                              name="pickup_city"
                              value={formData.pickup_city}
                              onChange={handleChange}
                              required
                              placeholder="Charlottesville"
                              data-testid="input-pickup-city"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>ZIP Code *</Label>
                            <Input
                              name="pickup_zip"
                              value={formData.pickup_zip}
                              onChange={handleChange}
                              required
                              placeholder="22903"
                              data-testid="input-pickup-zip"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Delivery Info */}
                {selectedSchool && (
                  <motion.div
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <h3 className="text-lg font-bold text-navy mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      <Package className="w-5 h-5 inline-block mr-2 text-red-600" />
                      Fall Delivery Details
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Delivery Location *</Label>
                        <Select
                          value={formData.delivery_location}
                          onValueChange={handleDeliveryLocationSelect}
                        >
                          <SelectTrigger data-testid="select-delivery-location">
                            <SelectValue placeholder="Select dorm / residence" />
                          </SelectTrigger>
                          <SelectContent>
                            {dorms.map((dorm) => (
                              <SelectItem key={dorm} value={dorm}>
                                {dorm}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Delivery Date *</Label>
                        <Select
                          value={formData.delivery_date}
                          onValueChange={(val) => {
                            const dateObj = selectedSchool.delivery_dates.find(d => d.date === val);
                            setFormData(prev => ({ ...prev, delivery_date: val, delivery_date_surcharge: dateObj?.surcharge || 0 }));
                          }}
                        >
                          <SelectTrigger data-testid="select-delivery-date">
                            <SelectValue placeholder="Select delivery date" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedSchool.delivery_dates.map((dateObj) => (
                              <SelectItem key={dateObj.date} value={dateObj.date}>
                                {new Date(dateObj.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                {dateObj.type === 'custom' ? ` (+$${dateObj.surcharge} custom)` : ' (Free)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {formData.delivery_location === 'Off-Campus' && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                        <p className="text-sm font-medium text-navy flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-red-600" />
                          Off-Campus Delivery Address
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2 sm:col-span-2">
                            <Label>Street Address *</Label>
                            <Input
                              name="delivery_address"
                              value={formData.delivery_address}
                              onChange={handleChange}
                              required
                              placeholder="456 Oak Avenue"
                              data-testid="input-delivery-address"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Apt / Suite</Label>
                            <Input
                              name="delivery_apartment"
                              value={formData.delivery_apartment}
                              onChange={handleChange}
                              placeholder="Suite 2A"
                              data-testid="input-delivery-apartment"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>City *</Label>
                            <Input
                              name="delivery_city"
                              value={formData.delivery_city}
                              onChange={handleChange}
                              required
                              placeholder="Charlottesville"
                              data-testid="input-delivery-city"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>ZIP Code *</Label>
                            <Input
                              name="delivery_zip"
                              value={formData.delivery_zip}
                              onChange={handleChange}
                              required
                              placeholder="22903"
                              data-testid="input-delivery-zip"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Marketing Source */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-navy mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    How Did You Hear About Us? *
                  </h3>
                  <div className="space-y-4">
                    <Select
                      value={formData.how_heard_about_us}
                      onValueChange={(val) => setFormData(prev => ({ ...prev, how_heard_about_us: val, how_heard_other: '' }))}
                    >
                      <SelectTrigger data-testid="select-how-heard">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {marketingSources.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {formData.how_heard_about_us === 'Other' && (
                      <Input
                        name="how_heard_other"
                        value={formData.how_heard_other}
                        onChange={handleChange}
                        placeholder="Please specify..."
                        data-testid="input-how-heard-other"
                      />
                    )}
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="bg-navy rounded-xl p-6 text-white">
                  <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Payment Summary
                  </h3>
                  <div className="space-y-2 text-gray-300">
                    <div className="flex justify-between">
                      <span>Reservation Deposit</span>
                      <span className="text-white font-bold">$30.00</span>
                    </div>
                    {formData.pickup_date_surcharge > 0 && (
                      <div className="flex justify-between">
                        <span>Custom Pickup Date</span>
                        <span className="text-yellow-300 font-bold">+${formData.pickup_date_surcharge}.00</span>
                      </div>
                    )}
                    {formData.delivery_date_surcharge > 0 && (
                      <div className="flex justify-between">
                        <span>Custom Delivery Date</span>
                        <span className="text-yellow-300 font-bold">+${formData.delivery_date_surcharge}.00</span>
                      </div>
                    )}
                    <div className="border-t border-white/20 pt-2 mt-2 flex justify-between text-white font-bold text-lg">
                      <span>Total Due Now</span>
                      <span>${30 + (formData.pickup_date_surcharge || 0) + (formData.delivery_date_surcharge || 0)}.00</span>
                    </div>
                    <div className="text-sm mt-2 text-gray-400">
                      * Remaining balance invoiced after pickup. Monthly storage rates vary by item.
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="btn-primary w-full py-4 text-base"
                  disabled={isSubmitting}
                  data-testid="submit-reservation-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ${30 + (formData.pickup_date_surcharge || 0) + (formData.delivery_date_surcharge || 0)} & Reserve
                    </>
                  )}
                </Button>
              </motion.form>
            )}

            {/* Contact Form */}
            {activeTab === 'contact' && (
              contactSubmitted ? (
                <motion.div
                  className="bg-white rounded-2xl p-8 text-center"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Message Sent!
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <Button
                    onClick={() => setContactSubmitted(false)}
                    className="btn-secondary"
                    data-testid="send-another-message-btn"
                  >
                    Send Another Message
                  </Button>
                </motion.div>
              ) : (
                <motion.form
                  onSubmit={handleContactSubmit}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 space-y-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  data-testid="contact-form"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_name">Name *</Label>
                      <Input
                        id="contact_name"
                        name="name"
                        value={contactData.name}
                        onChange={handleContactChange}
                        required
                        placeholder="Your name"
                        data-testid="input-contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_email">Email *</Label>
                      <Input
                        id="contact_email"
                        name="email"
                        type="email"
                        value={contactData.email}
                        onChange={handleContactChange}
                        required
                        placeholder="your@email.com"
                        data-testid="input-contact-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_subject">Subject *</Label>
                    <Input
                      id="contact_subject"
                      name="subject"
                      value={contactData.subject}
                      onChange={handleContactChange}
                      required
                      placeholder="How can we help?"
                      data-testid="input-contact-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_message">Message *</Label>
                    <Textarea
                      id="contact_message"
                      name="message"
                      value={contactData.message}
                      onChange={handleContactChange}
                      required
                      placeholder="Your message..."
                      rows={5}
                      data-testid="input-contact-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="btn-secondary w-full py-4"
                    disabled={isSubmitting}
                    data-testid="submit-contact-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </motion.form>
              )
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
